import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  RouteLinePoint as RouteLinePointSchema,
  type Activity,
  type RouteFromGeometryRequest,
  type RouteLinePoint,
  type RoutePointKind,
  type RoutingPreviewRequest,
} from '@xingroute/schema'
import { trackStats } from '../../lib/gpx.js'
import { snapPointToLine } from '../../lib/routeGeometry.js'
import type { DrawingVertex } from './DrawingMap.js'

export interface DrawingPoi extends DrawingVertex {
  name: string
  desc: string
  kind: RoutePointKind
}

export interface RouteSegment {
  routed: boolean
  line: RouteLinePoint[]
}

type RoutingProfile = RoutingPreviewRequest['profile']

export type RoutingRequestFn = (
  points: RoutingPreviewRequest['points'],
  profile: RoutingProfile,
) => Promise<RouteLinePoint[]>

interface RoutingTask {
  generation: number
  segmentIndex: number
  from: DrawingVertex
  to: DrawingVertex
  profile: RoutingProfile
}

type TFn = (key: string, options?: Record<string, unknown>) => string

export interface UseTrackDrawStateOptions {
  disabled?: boolean
  onDraftChange?: () => void
  previewLine?: { lat: number; lon: number }[] | null
  routeSegment?: RoutingRequestFn
  defaultName?: string
  t: TFn
  initialActivity?: Activity
  lockActivity?: boolean
}

export interface TrackDrawState {
  vertices: DrawingVertex[]
  pois: DrawingPoi[]
  name: string
  activities: Activity[]
  drawingTool: 'line' | 'poi'
  routingEnabled: boolean
  routedPreviewLine: RouteLinePoint[]
  segments: RouteSegment[]
  lockActivity: boolean
  hasDraft: boolean
  stats: ReturnType<typeof trackStats> | null
  vertexLimit: number
  canFinish: boolean
  setName: (value: string) => void
  setActivities: (value: Activity[]) => void
  setDrawingTool: (value: 'line' | 'poi') => void
  setRoutingEnabled: (value: boolean) => void
  addVertex: (lng: number, lat: number) => void
  insertVertex: (index: number, lng: number, lat: number) => void
  updateVertex: (index: number, lng: number, lat: number) => void
  deleteVertex: (index: number) => void
  moveVertex: (index: number, direction: -1 | 1) => void
  beginMoveVertex: (index: number) => void
  endMoveVertex: () => void
  addPoi: (lng: number, lat: number) => void
  updatePoi: (index: number, patch: Partial<Pick<DrawingPoi, 'name' | 'desc' | 'kind'>>) => void
  updatePoiPosition: (index: number, lng: number, lat: number) => void
  deletePoi: (index: number) => void
  movePoi: (index: number, direction: -1 | 1) => void
  beginMovePoi: (index: number) => void
  endMovePoi: () => void
  undo: () => void
  clear: () => void
  reset: () => void
  buildRequest: () => RouteFromGeometryRequest | null
}

const VERTEX_LIMIT = 500
const POI_LIMIT = 100

function toLinePoint(vertex: DrawingVertex): Pick<RouteLinePoint, 'lat' | 'lon'> {
  return { lat: vertex.lat, lon: vertex.lng }
}

function buildStraightSegment(from: DrawingVertex, to: DrawingVertex): RouteSegment {
  return {
    routed: false,
    line: [toLinePoint(from), toLinePoint(to)],
  }
}

function buildStraightSegments(vertices: DrawingVertex[]): RouteSegment[] {
  const next: RouteSegment[] = []
  for (let index = 1; index < vertices.length; index += 1) {
    next.push(buildStraightSegment(vertices[index - 1]!, vertices[index]!))
  }
  return next
}

function cloneVertices(vertices: DrawingVertex[]): DrawingVertex[] {
  return vertices.map((vertex) => ({ ...vertex }))
}

function clonePois(pois: DrawingPoi[]): DrawingPoi[] {
  return pois.map((poi) => ({ ...poi }))
}

function cloneSegments(segments: RouteSegment[]): RouteSegment[] {
  return segments.map((segment) => ({
    routed: segment.routed,
    line: segment.line.map((point) => ({ ...point })),
  }))
}

function sameVertex(
  left: DrawingVertex | null | undefined,
  right: DrawingVertex | null | undefined,
): boolean {
  return left?.lat === right?.lat && left?.lng === right?.lng
}

function sameLinePoint(
  left: RouteLinePoint | undefined,
  right: RouteLinePoint | undefined,
): boolean {
  return left?.lat === right?.lat && left?.lon === right?.lon
}

function isValidRouteLine(line: RouteLinePoint[]): boolean {
  return line.length >= 2 && line.every((point) => RouteLinePointSchema.safeParse(point).success)
}

function segmentMatchesVertices(
  vertices: DrawingVertex[],
  segmentIndex: number,
  from: DrawingVertex,
  to: DrawingVertex,
): boolean {
  return sameVertex(vertices[segmentIndex], from) && sameVertex(vertices[segmentIndex + 1], to)
}

export function useTrackDrawState(options: UseTrackDrawStateOptions): TrackDrawState {
  const {
    disabled = false,
    onDraftChange,
    previewLine,
    defaultName,
    t,
    lockActivity = false,
  } = options
  const [vertices, setVertices] = useState<DrawingVertex[]>([])
  const [pois, setPois] = useState<DrawingPoi[]>([])
  const [name, setName] = useState<string>(() => defaultName ?? defaultDrawingName(t))
  const [activities, setActivities] = useState<Activity[]>([options.initialActivity ?? 'hiking'])
  const lockedActivity = lockActivity ? options.initialActivity : undefined
  useEffect(() => {
    if (lockedActivity) setActivities([lockedActivity])
  }, [lockedActivity])
  const [drawingTool, setDrawingTool] = useState<'line' | 'poi'>('line')
  const [segments, setSegments] = useState<RouteSegment[]>([])
  const [routingEnabled, setRoutingEnabledState] = useState(true)
  const routingQueueRef = useRef<RoutingTask[]>([])
  const routingBusyRef = useRef(false)
  const routingActiveGenerationsRef = useRef(new Set<number>())
  const routingGenerationRef = useRef(0)
  const routeSegmentRef = useRef(options.routeSegment)
  const verticesRef = useRef<DrawingVertex[]>([])
  const poisRef = useRef<DrawingPoi[]>([])
  const segmentsRef = useRef<RouteSegment[]>([])
  const movingRef = useRef<number | null>(null)
  const movingVertexStartRef = useRef<DrawingVertex | null>(null)
  const dragHistoryPushedRef = useRef(false)
  const historyRef = useRef<
    Array<{ vertices: DrawingVertex[]; pois: DrawingPoi[]; segments: RouteSegment[] }>
  >([])
  const hasDraft = vertices.length > 0 || pois.length > 0

  const routeSegmentAvailable = Boolean(options.routeSegment)
  const routingProfile = useMemo(
    () => (activities.includes('cycling' as Activity) ? 'bike' : 'foot') as RoutingProfile,
    [activities],
  )
  const previousRoutingProfileRef = useRef<RoutingProfile>(routingProfile)
  const previousRouteSegmentAvailableRef = useRef(routeSegmentAvailable)

  useEffect(() => {
    routeSegmentRef.current = options.routeSegment
  }, [options.routeSegment])

  useEffect(() => {
    verticesRef.current = vertices
    poisRef.current = pois
    segmentsRef.current = segments
  }, [pois, segments, vertices])

  const routedPreviewLine = useMemo(() => {
    if (segments.length === 0) return []
    const result: RouteLinePoint[] = []
    for (const segment of segments) {
      for (let index = 0; index < segment.line.length; index += 1) {
        const point = segment.line[index]!
        if (index === 0 && sameLinePoint(result[result.length - 1], point)) continue
        result.push(point)
      }
    }
    return result
  }, [segments])

  const pushHistory = useCallback(() => {
    historyRef.current.push({
      vertices: cloneVertices(verticesRef.current),
      pois: clonePois(poisRef.current),
      segments: cloneSegments(segmentsRef.current),
    })
    if (historyRef.current.length > 100) historyRef.current.shift()
    onDraftChange?.()
  }, [onDraftChange])

  const startRoutingGeneration = useCallback(() => {
    routingGenerationRef.current += 1
    routingQueueRef.current = []
    return routingGenerationRef.current
  }, [])

  const processRoutingQueue = useCallback(async (generation = routingGenerationRef.current) => {
    if (!routeSegmentRef.current) return
    if (routingBusyRef.current && routingActiveGenerationsRef.current.has(generation)) return
    routingActiveGenerationsRef.current.add(generation)
    routingBusyRef.current = true
    try {
      while (routingQueueRef.current.length > 0) {
        if (generation !== routingGenerationRef.current) return
        const task = routingQueueRef.current.shift()!
        const { generation: taskGeneration, profile, segmentIndex, from, to } = task
        if (taskGeneration !== generation || taskGeneration !== routingGenerationRef.current)
          continue
        const routeSegment = routeSegmentRef.current
        if (!routeSegment) continue
        try {
          const line = await routeSegment(
            [
              { lat: from.lat, lon: from.lng },
              { lat: to.lat, lon: to.lng },
            ],
            profile,
          )
          if (!isValidRouteLine(line)) continue
          setSegments((prev) => {
            if (segmentIndex >= prev.length) return prev
            if (taskGeneration !== routingGenerationRef.current) return prev
            if (!segmentMatchesVertices(verticesRef.current, segmentIndex, from, to)) return prev
            const next = [...prev]
            next[segmentIndex] = { routed: true, line }
            segmentsRef.current = next
            return next
          })
        } catch {
          setSegments((prev) => {
            if (segmentIndex >= prev.length) return prev
            if (taskGeneration !== routingGenerationRef.current) return prev
            if (!segmentMatchesVertices(verticesRef.current, segmentIndex, from, to)) return prev
            return prev
          })
        }
      }
    } finally {
      routingActiveGenerationsRef.current.delete(generation)
      routingBusyRef.current = routingActiveGenerationsRef.current.size > 0
      if (generation === routingGenerationRef.current && routingQueueRef.current.length > 0) {
        void processRoutingQueue(generation)
      }
    }
  }, [])

  const enqueueRouting = useCallback(
    (
      segmentIndex: number,
      from: DrawingVertex,
      to: DrawingVertex,
      profile: RoutingProfile,
      generation: number,
    ) => {
      routingQueueRef.current.push({ generation, segmentIndex, from, to, profile })
      void processRoutingQueue(generation)
    },
    [processRoutingQueue],
  )

  const enqueueAllSegments = useCallback(
    (nextVertices: DrawingVertex[], profile: RoutingProfile, generation: number) => {
      if (!routeSegmentRef.current) return
      for (let index = 1; index < nextVertices.length; index += 1) {
        enqueueRouting(
          index - 1,
          nextVertices[index - 1]!,
          nextVertices[index]!,
          profile,
          generation,
        )
      }
    },
    [enqueueRouting],
  )

  const rebuildSegments = useCallback(
    (nextVertices: DrawingVertex[], reroute: boolean) => {
      const generation = startRoutingGeneration()
      const nextSegments = buildStraightSegments(nextVertices)
      verticesRef.current = nextVertices
      segmentsRef.current = nextSegments
      setSegments(nextSegments)
      if (reroute && routeSegmentRef.current) {
        enqueueAllSegments(nextVertices, routingProfile, generation)
      }
    },
    [enqueueAllSegments, routingProfile, startRoutingGeneration],
  )

  const addVertex = useCallback(
    (lng: number, lat: number) => {
      if (disabled) return
      setVertices((prev) => {
        if (prev.length >= VERTEX_LIMIT) return prev
        pushHistory()
        const newVertex = { lng, lat }
        const newVertices = [...prev, newVertex]
        verticesRef.current = newVertices
        if (prev.length >= 1) {
          const fromVertex = prev[prev.length - 1]!
          const segmentIndex = prev.length - 1
          const nextSegments = [...segmentsRef.current, buildStraightSegment(fromVertex, newVertex)]
          segmentsRef.current = nextSegments
          setSegments(nextSegments)
          if (routingEnabled && routeSegmentRef.current) {
            enqueueRouting(
              segmentIndex,
              fromVertex,
              newVertex,
              routingProfile,
              routingGenerationRef.current,
            )
          }
        }
        return newVertices
      })
    },
    [disabled, enqueueRouting, pushHistory, routingEnabled, routingProfile],
  )

  const undo = useCallback(() => {
    if (disabled) return
    const previous = historyRef.current.pop()
    if (!previous) return
    startRoutingGeneration()
    onDraftChange?.()
    verticesRef.current = previous.vertices
    poisRef.current = previous.pois
    segmentsRef.current = previous.segments
    setVertices(previous.vertices)
    setPois(previous.pois)
    setSegments(previous.segments)
  }, [disabled, onDraftChange, startRoutingGeneration])

  const clear = useCallback(() => {
    if (disabled || !hasDraft) return
    if (typeof window !== 'undefined' && !window.confirm(t('editor.draw.clearConfirm'))) return
    pushHistory()
    startRoutingGeneration()
    verticesRef.current = []
    poisRef.current = []
    segmentsRef.current = []
    setVertices([])
    setPois([])
    setSegments([])
  }, [disabled, hasDraft, pushHistory, startRoutingGeneration, t])

  const reset = useCallback(() => {
    historyRef.current = []
    startRoutingGeneration()
    verticesRef.current = []
    poisRef.current = []
    segmentsRef.current = []
    setVertices([])
    setPois([])
    setSegments([])
  }, [startRoutingGeneration])

  const deleteVertex = useCallback(
    (index: number) => {
      if (disabled) return
      setVertices((prev) => {
        if (index < 0 || index >= prev.length) return prev
        pushHistory()
        const nextVertices = prev.filter((_, currentIndex) => currentIndex !== index)
        const generation = startRoutingGeneration()
        verticesRef.current = nextVertices
        const nextSegments =
          index === 0
            ? segmentsRef.current.slice(1)
            : index === prev.length - 1
              ? segmentsRef.current.slice(0, -1)
              : (() => {
                  const next = [...segmentsRef.current]
                  next.splice(
                    index - 1,
                    2,
                    buildStraightSegment(prev[index - 1]!, prev[index + 1]!),
                  )
                  return next
                })()
        segmentsRef.current = nextSegments
        setSegments(nextSegments)
        if (routingEnabled && routeSegmentRef.current) {
          for (let segmentIndex = 0; segmentIndex < nextSegments.length; segmentIndex += 1) {
            if (nextSegments[segmentIndex]?.routed) continue
            const fromVertex = nextVertices[segmentIndex]
            const toVertex = nextVertices[segmentIndex + 1]
            if (!fromVertex || !toVertex) continue
            enqueueRouting(segmentIndex, fromVertex, toVertex, routingProfile, generation)
          }
        }
        return nextVertices
      })
    },
    [disabled, enqueueRouting, pushHistory, routingEnabled, routingProfile, startRoutingGeneration],
  )

  const insertVertex = useCallback(
    (index: number, lng: number, lat: number) => {
      if (disabled || vertices.length >= VERTEX_LIMIT) return
      pushHistory()
      setVertices((prev) => {
        const next = [...prev]
        next.splice(index, 0, { lng, lat })
        rebuildSegments(next, routingEnabled)
        return next
      })
    },
    [disabled, pushHistory, rebuildSegments, routingEnabled, vertices.length],
  )

  const updateVertex = useCallback(
    (index: number, lng: number, lat: number) => {
      if (disabled) return
      const isDragging = movingRef.current === index
      const nextVertex = { lng, lat }
      if (!isDragging) pushHistory()
      if (
        isDragging &&
        !dragHistoryPushedRef.current &&
        !sameVertex(movingVertexStartRef.current, nextVertex)
      ) {
        pushHistory()
        dragHistoryPushedRef.current = true
      }
      if (isDragging) {
        verticesRef.current = verticesRef.current.map((vertex, currentIndex) =>
          currentIndex === index ? nextVertex : vertex,
        )
      }
      setVertices((prev) => {
        const next = prev.map((vertex, currentIndex) =>
          currentIndex === index ? nextVertex : vertex,
        )
        verticesRef.current = next
        if (!isDragging) rebuildSegments(next, routingEnabled)
        return next
      })
    },
    [disabled, pushHistory, rebuildSegments, routingEnabled],
  )

  const addPoi = useCallback(
    (lng: number, lat: number) => {
      if (disabled) return
      setPois((prev) => {
        if (prev.length >= POI_LIMIT) return prev
        pushHistory()
        const line = previewLine ?? vertices.map((vertex) => ({ lat: vertex.lat, lon: vertex.lng }))
        const snapped = snapPointToLine({ lat, lon: lng }, line, 5)
        const nextPois: DrawingPoi[] = [
          ...prev,
          {
            lng: snapped?.lon ?? lng,
            lat: snapped?.lat ?? lat,
            name: '',
            desc: '',
            kind: 'generic',
          },
        ]
        poisRef.current = nextPois
        return nextPois
      })
    },
    [disabled, previewLine, pushHistory, vertices],
  )

  const updatePoi = useCallback(
    (index: number, patch: Partial<Pick<DrawingPoi, 'name' | 'desc' | 'kind'>>) => {
      if (disabled) return
      pushHistory()
      setPois((prev) => {
        const nextPois = prev.map((poi, currentIndex) =>
          currentIndex === index ? { ...poi, ...patch } : poi,
        )
        poisRef.current = nextPois
        return nextPois
      })
    },
    [disabled, pushHistory],
  )

  const movePoi = useCallback(
    (index: number, direction: -1 | 1) => {
      if (disabled) return
      const target = index + direction
      if (target < 0 || target >= pois.length) return
      pushHistory()
      setPois((prev) => {
        const next = [...prev]
        const current = next[index]!
        next[index] = next[target]!
        next[target] = current
        poisRef.current = next
        return next
      })
    },
    [disabled, pois.length, pushHistory],
  )

  const beginMovePoi = useCallback(
    (_index: number) => {
      if (disabled) return
      pushHistory()
    },
    [disabled, pushHistory],
  )

  const updatePoiPosition = useCallback(
    (index: number, lng: number, lat: number) => {
      if (disabled) return
      setPois((prev) => {
        const nextPois = prev.map((poi, currentIndex) =>
          currentIndex === index ? { ...poi, lng, lat } : poi,
        )
        poisRef.current = nextPois
        return nextPois
      })
    },
    [disabled],
  )

  const endMovePoi = useCallback(() => {}, [])

  const deletePoi = useCallback(
    (index: number) => {
      if (disabled) return
      pushHistory()
      setPois((prev) => {
        const nextPois = prev.filter((_, currentIndex) => currentIndex !== index)
        poisRef.current = nextPois
        return nextPois
      })
    },
    [disabled, pushHistory],
  )

  const beginMoveVertex = useCallback(
    (index: number) => {
      if (disabled) return
      movingRef.current = index
      movingVertexStartRef.current = verticesRef.current[index] ?? null
      dragHistoryPushedRef.current = false
    },
    [disabled],
  )

  const endMoveVertex = useCallback(() => {
    const movingIndex = movingRef.current
    const startVertex = movingVertexStartRef.current
    movingRef.current = null
    movingVertexStartRef.current = null
    const dragged = dragHistoryPushedRef.current
    dragHistoryPushedRef.current = false
    if (movingIndex === null || !startVertex) return
    if (!dragged && sameVertex(startVertex, verticesRef.current[movingIndex])) return
    rebuildSegments(verticesRef.current, routingEnabled)
  }, [rebuildSegments, routingEnabled])

  const moveVertex = useCallback(
    (index: number, direction: -1 | 1) => {
      if (disabled) return
      const target = index + direction
      if (target < 0 || target >= vertices.length) return
      pushHistory()
      setVertices((prev) => {
        const next = [...prev]
        const current = next[index]!
        next[index] = next[target]!
        next[target] = current
        rebuildSegments(next, routingEnabled)
        return next
      })
    },
    [disabled, pushHistory, rebuildSegments, routingEnabled, vertices.length],
  )

  useEffect(() => {
    const profileChanged = previousRoutingProfileRef.current !== routingProfile
    const routeSegmentAvailabilityChanged =
      previousRouteSegmentAvailableRef.current !== routeSegmentAvailable
    previousRoutingProfileRef.current = routingProfile
    previousRouteSegmentAvailableRef.current = routeSegmentAvailable

    if (vertices.length < 2) return
    if (profileChanged && routingEnabled && routeSegmentAvailable) {
      rebuildSegments(vertices, true)
      return
    }
    if (routeSegmentAvailabilityChanged) {
      rebuildSegments(vertices, routingEnabled && routeSegmentAvailable)
    }
  }, [rebuildSegments, routeSegmentAvailable, routingEnabled, routingProfile, vertices])

  const setRoutingEnabled = useCallback(
    (value: boolean) => {
      if (value === routingEnabled) return
      setRoutingEnabledState(value)
      rebuildSegments(verticesRef.current, value && routeSegmentAvailable)
    },
    [rebuildSegments, routeSegmentAvailable, routingEnabled],
  )

  const stats = useMemo(() => {
    const source =
      routedPreviewLine.length >= 2
        ? routedPreviewLine
        : previewLine && previewLine.length >= 2
          ? previewLine
          : vertices.map((vertex) => ({ lat: vertex.lat, lon: vertex.lng }))
    if (source.length < 2) return null
    return trackStats(source)
  }, [previewLine, routedPreviewLine, vertices])

  const canFinish = vertices.length >= 2 && vertices.length <= VERTEX_LIMIT

  const buildRequest = useCallback((): RouteFromGeometryRequest | null => {
    if (!canFinish) return null
    const safeName = sanitizeName(name) || sanitizeName(defaultDrawingName(t)) || 'drawn-route'
    const line =
      routedPreviewLine.length >= 2
        ? routedPreviewLine
        : vertices.map((vertex) => ({ lat: vertex.lat, lon: vertex.lng }))
    return {
      activities,
      title: safeName,
      geometry_type: 'line',
      line,
      points: pois.map((poi, index) => ({
        lat: poi.lat,
        lon: poi.lng,
        name: poi.name || t('editor.draw.poiName', { count: index + 1 }),
        desc: poi.desc || undefined,
        kind: poi.kind,
      })),
      source: {
        kind: 'drawn',
        routing_mode: routingEnabled ? 'routing' : 'manual',
      },
    }
  }, [activities, canFinish, name, pois, routedPreviewLine, routingEnabled, t, vertices])

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        if (disabled) return
        event.preventDefault()
        undo()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [disabled, undo])

  return {
    vertices,
    pois,
    name,
    activities,
    drawingTool,
    routingEnabled,
    routedPreviewLine,
    segments,
    lockActivity,
    hasDraft,
    stats,
    vertexLimit: VERTEX_LIMIT,
    canFinish,
    setName,
    setActivities,
    setDrawingTool,
    setRoutingEnabled,
    addVertex,
    insertVertex,
    updateVertex,
    deleteVertex,
    moveVertex,
    beginMoveVertex,
    endMoveVertex,
    addPoi,
    updatePoi,
    updatePoiPosition,
    deletePoi,
    movePoi,
    beginMovePoi,
    endMovePoi,
    undo,
    clear,
    reset,
    buildRequest,
  }
}

export function defaultDrawingName(t: TFn): string {
  const today = new Date().toISOString().slice(0, 10)
  return t('editor.draw.defaultName', { date: today })
}

export function sanitizeName(input: string): string {
  return input.trim().slice(0, 80)
}
